(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-ct-native-reward]';
  const STYLES_SELECTOR = '[data-ct-native-reward-styles]';
  const CAMPAIGN_CLASS = 'ct-cw-campaign-active';
  const APPLIED_PARAM = 'ct_reward_applied';

  const init = (root) => {
    if (root.dataset.ctInitialized === 'true') return;

    const dialog = root.querySelector('[data-ct-reward-dialog]');
    const openButton = root.querySelector('[data-ct-reward-open]');
    const dismissButton = root.querySelector('[data-ct-reward-dismiss]');
    const form = root.querySelector('#CtNativeRewardForm');
    const confirmation = root.querySelector('[data-ct-reward-confirmation]');
    const fallbackAction = root.querySelector('[data-ct-reward-action]');
    let autoOpenTarget = dialog?.querySelector('[data-ct-reward-auto-open]');

    if (!dialog || !openButton || !dismissButton) return;
    root.dataset.ctInitialized = 'true';

    const campaignIsActive = () => document.documentElement.classList.contains(CAMPAIGN_CLASS);

    const open = (focusTarget = null) => {
      if (campaignIsActive() || root.hidden) return;

      root.dataset.open = 'true';
      openButton.setAttribute('aria-expanded', 'true');

      if (typeof dialog.showModal === 'function') {
        if (!dialog.open) dialog.showModal();
      } else {
        dialog.setAttribute('open', '');
      }

      if (focusTarget instanceof HTMLElement) {
        requestAnimationFrame(() => focusTarget.focus({ preventScroll: true }));
      }
    };

    const close = ({ restoreFocus = true } = {}) => {
      root.dataset.open = 'false';
      openButton.setAttribute('aria-expanded', 'false');

      if (typeof dialog.close === 'function' && dialog.open) dialog.close();
      else dialog.removeAttribute('open');

      if (restoreFocus && document.contains(openButton) && !campaignIsActive() && !root.hidden) {
        openButton.focus({ preventScroll: true });
      }
    };

    const dismiss = () => {
      close({ restoreFocus: false });
      root.hidden = true;
      root.dataset.dismissed = 'true';
    };

    const currentDestination = () => {
      const destination = new URL(window.location.href);
      destination.hash = '';
      destination.searchParams.delete('customer_posted');
      destination.searchParams.delete('form_type');
      destination.searchParams.delete(APPLIED_PARAM);

      const productForm = document.querySelector(
        'form[id^="product-form-"], form[data-type="add-to-cart-form"]'
      );
      const variantInput = productForm?.querySelector('input[name="id"]');

      if (destination.pathname.includes('/products/') && variantInput?.value) {
        destination.searchParams.set('variant', variantInput.value);
      }

      destination.searchParams.set(APPLIED_PARAM, '1');
      return `${destination.pathname}${destination.search}`;
    };

    const discountDestination = () => {
      const discountUrl = new URL(
        root.dataset.ctRewardDiscountUrl || fallbackAction?.href || '/discount/CHETIENDA55',
        window.location.origin
      );
      discountUrl.searchParams.set('redirect', currentDestination());
      return `${discountUrl.pathname}${discountUrl.search}`;
    };

    const updateDestinations = () => {
      const destination = discountDestination();
      const returnToInput = form?.querySelector('input[name="return_to"]');

      if (returnToInput instanceof HTMLInputElement) returnToInput.value = destination;
      if (fallbackAction instanceof HTMLAnchorElement) fallbackAction.href = destination;

      return destination;
    };

    const consumeAppliedMarker = () => {
      const currentUrl = new URL(window.location.href);
      if (currentUrl.searchParams.get(APPLIED_PARAM) !== '1' || !confirmation) return false;

      currentUrl.searchParams.delete(APPLIED_PARAM);
      currentUrl.searchParams.delete('customer_posted');
      currentUrl.searchParams.delete('form_type');
      window.history.replaceState(
        window.history.state,
        '',
        `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`
      );

      if (form instanceof HTMLFormElement) form.hidden = true;
      confirmation.hidden = false;
      dialog.setAttribute('aria-labelledby', 'CtNativeRewardAppliedTitle');
      autoOpenTarget = confirmation;
      return true;
    };

    openButton.addEventListener('click', () => open());
    dismissButton.addEventListener('click', dismiss);
    form?.addEventListener('submit', updateDestinations);

    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      close();
    });

    dialog.addEventListener('close', () => {
      root.dataset.open = 'false';
      openButton.setAttribute('aria-expanded', 'false');
    });

    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) close();
    });

    let stylesRevealed = false;
    const revealAfterStyles = () => {
      if (stylesRevealed) return;

      stylesRevealed = true;
      root.dataset.ctStylesReady = 'true';

      if (autoOpenTarget) requestAnimationFrame(() => open(autoOpenTarget));
    };

    consumeAppliedMarker();

    if (fallbackAction instanceof HTMLAnchorElement) {
      window.location.replace(updateDestinations());
      return;
    }

    const styles = document.querySelector(STYLES_SELECTOR);
    if (!styles || styles.dataset.ctReady === 'true' || styles.media === 'all') {
      revealAfterStyles();
    } else {
      styles.addEventListener('load', revealAfterStyles, { once: true });
      styles.addEventListener('error', revealAfterStyles, { once: true });
    }

    const campaignObserver = new MutationObserver(() => {
      if (campaignIsActive() && dialog.open) close({ restoreFocus: false });
    });
    campaignObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
  };

  const initAll = (scope = document) => {
    scope.querySelectorAll(ROOT_SELECTOR).forEach(init);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }
})();
